// Validate name field according to specified rules
export const validateName = (name) => {
  // Scenario 1: Field left blank
  if (!name || name.trim().length === 0) {
    return "Please enter a valid name.";
  }

  // Scenario 3: Multiple consecutive spaces
  if (/\s{2,}/.test(name)) {
    return "Extra spaces detected — please remove them.";
  }

  if (/\.{2,}|^\.|\.$/.test(name.trim())) {
    return "Invalid input detected. Please enter a valid name.";
  }

  // Scenario 4: Leading or trailing spaces
  if (name !== name.trim()) {
    return "Name field cannot start or end with a space.";
  }

  // Scenario 5: Unescaped characters or code injection attempt
  // Check for HTML tags, script tags, or suspicious patterns
  if (/<|>|&lt;|&gt;|<script|javascript:|eval\(|onclick|onerror/i.test(name)) {
    return "Invalid input detected. Please enter a valid name.";
  }
  const words = name.trim().split(/\s+/);

  for (let word of words) {
    const cleanWord = word.replace(/[.''\-]/g, '');

    if (/\d/.test(cleanWord)) {
      const validSuffixPattern = /^(Jr|Sr|I{1,3}|IV|V|VI{0,3}|IX|X|1st|2nd|3rd|\d+th)$/i;

      if (validSuffixPattern.test(cleanWord)) {
        continue;
      }

      const romanNumeralPattern = /^[IVXLCDM]+$/i;
      if (romanNumeralPattern.test(cleanWord)) {
        continue;
      }

      return "Please enter only alphabetic characters.";
    }
  }

  const invalidCharRegex = /[^a-zA-Z\s.'’\-]/; // only allow these characters
  const repeatingSpecialsRegex = /([.'’\-])\1+/; // disallow repeated special characters

  if (invalidCharRegex.test(name) || repeatingSpecialsRegex.test(name)) {
    return "Invalid input detected. Please enter a valid name.";
  }

  if (invalidCharRegex.test(name)) {
    return "Please enter only alphabetic characters.";
  }


  return "";
};

// Calculate page numbers for table of contents
export const calculatePageNumbers = (menuItems, pageComponentMap) => {
  // Table of contents sections in order
  const tocSections = [
    "OVERVIEW",
    "SUMMARY SNAPSHOT",
    "STARTER QUADRANT",
    "QUADRANT FLOW",
    "LEADERSHIP STYLE",
    "MINDSET",
    "REFLECT AND INTEGRATE"
  ];

  // Mapping from TOC sections to their corresponding menu keys (in order)
  const contentMapping = {
    "OVERVIEW": ["Overview"],
    "SUMMARY SNAPSHOT": ["Summary Snapshot"],
    "STARTER QUADRANT": ["Starter Quadrant", "Four Quadrants", "Starter Quadrant Chart", "Results"],
    "QUADRANT FLOW": ["HomeQuadrantFlow", "Introduction", "Quadrant Flow", "QFItemOnePage", "Heart", "Soul", "Mind", "Strength"],
    "LEADERSHIP STYLE": ["Home Leadership Style", "Leadership Style Introduction", "Leadership Style Chart", "Leadership Style Results", "Leadership Flow Introduction", "Leadership Flow Results", "Items Overview", "Collaborative", "Visionary", "Strategic", "Performance", "Alignment"],
    "MINDSET": ["Home Mindset", "Mindset Introduction", "Mindset Results"],
    "REFLECT AND INTEGRATE": ["Home Reflect And Integrate", "Reflect and Integrate", "Life Integration", "Leadership Integration", "Final Thoughts"]
  };

  const findActiveMenuItems = (items) => {
    const activeItems = new Set();

    const processItem = (item) => {
      if (item.isActive) {
        activeItems.add(item.key);

        if (item.subMenu) {
          item.subMenu.forEach(subItem => {
            if (subItem.isActive) {
              activeItems.add(subItem.key);
              processItem(subItem);
            }
          });
        }
      } else if (item.subMenu) {
        item.subMenu.forEach(subItem => processItem(subItem));
      }
    };

    items?.forEach(item => processItem(item));
    return Array.from(activeItems);
  };

  const activeMenuKeys = findActiveMenuItems(menuItems || []);
  let currentPageNumber = 3; // Start from page 3 (Cover=1, TOC=2, Overview=3)
  const pageNumbers = [];

  // Calculate cumulative page numbers for each TOC section
  tocSections.forEach(tocSection => {
    const menuKeys = contentMapping[tocSection] || [];

    let sectionStartPage = currentPageNumber;
    let sectionHasActivePages = false;

    // Count all pages for this section
    menuKeys.forEach(menuKey => {
      const pageConfigs = pageComponentMap[menuKey];
      const isActive = activeMenuKeys.includes(menuKey);

      if (pageConfigs && isActive) {
        if (!sectionHasActivePages) {
          sectionStartPage = currentPageNumber;
          sectionHasActivePages = true;
        }

        // Add the number of pages for this menu key
        currentPageNumber += pageConfigs.length;
      }
    });

    // Add the section start page (or 0 if section has no active pages)
    const pageNumber = sectionHasActivePages ? sectionStartPage : 0;
    pageNumbers.push(pageNumber);
  });

  return pageNumbers;
};



export function formatDate(dateStr) {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  const day = date.getDate();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  // Add ordinal suffix to day
  const getOrdinalSuffix = (day) => {
    // if (day > 3 && day < 21) return 'th';
    if (day > 3 && day < 21) return "";
    switch (day % 10) {
      // case 1: return 'st';
      // case 2: return 'nd';
      // case 3: return 'rd';
      // default: return 'th';
      case 1:
        return "";
      case 2:
        return "";
      case 3:
        return "";
      default:
        return "";
    }
  };

  return `${month} ${year}`;
}


export const to1Decimal = (n) => Math.floor(n * 10) / 10

export const getMindsetSum = (variables) => {
  if (!Array.isArray(variables)) return 0;

  const mindsetVars = [
    "mind_set1", "mind_set2", "mind_set3", "mind_set4",
    "mind_set5", "mind_set6", "mind_set7", "mind_set8",
    "mind_set9", "mind_set10", "mind_set11", "mind_set12"
  ];

  let mindsetSum = 0;
  const mindSetSumVar = variables.find(v => v.variable === "mind_set_sum");

  if (mindSetSumVar && mindSetSumVar.score !== undefined) {
    mindsetSum = mindSetSumVar.score;
  } else {
    mindsetSum = mindsetVars.reduce((sum, mindsetVar) => {
      const variable = variables.find(v => v.variable === mindsetVar);
      return sum + (variable && variable.score !== undefined ? variable.score : 0);
    }, 0);
  }

  const score =  Math.round((mindsetSum / 84) * 100)
  return mindsetSum ;
};

export const categorizeMindset = (percentage) => {
  if (percentage < 36) {
    return "fixed";
  } else if (percentage >= 36 && percentage < 66) {
    return "conditional";
  } else {
    return "growth";
  }
};

export const categorizeMindsetCapitalized = (percentage) => {
  const category = categorizeMindset(Math.round(percentage));
  return category.charAt(0).toUpperCase() + category.slice(1);
};
